import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { GitBranch, Plus, Search, Loader2, ChevronRight, ChevronLeft } from 'lucide-react';
import { FlowCard, FlowFormDialog, TestFlowDialog, API_URL } from '../components/flows';

const DEFAULT_FORM_DATA = {
  name: '',
  description: '',
  task_ids: [],
  trigger_keywords: [],
  trigger_phrases: [],
  trigger_patterns: [],
  priority: 0,
  can_be_triggered: true,
  is_fallback: false,
  status: 'active'
};

export const FlowsPage = () => {
  const { t } = useTranslation('workflows');
  // List state
  const [flows, setFlows] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingFlow, setEditingFlow] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [keywordInput, setKeywordInput] = useState('');
  const [phraseInput, setPhraseInput] = useState('');
  const [patternInput, setPatternInput] = useState('');
  
  // Test state
  const [testDialog, setTestDialog] = useState(null);
  const [testMessage, setTestMessage] = useState('');
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    fetchFlows();
    fetchTasks();
  }, [page, statusFilter]);

  const fetchFlows = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '12'
      });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (search) params.append('search', search);
      
      const response = await axios.get(`${API_URL}/api/workflows/flows?${params}`);
      setFlows(response.data.data);
      setTotalPages(response.data.pagination.total_pages);
    } catch (error) {
      toast.error('Error al cargar flujos');
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/workflows/tasks?limit=100&status=active`);
      setTasks(response.data.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const openForm = async (flow = null) => {
    if (flow) {
      setEditingFlow(flow);
      setFormData({
        name: flow.name,
        description: flow.description || '',
        task_ids: flow.task_ids || [],
        trigger_keywords: flow.trigger_keywords || [],
        trigger_phrases: flow.trigger_phrases || [],
        trigger_patterns: flow.trigger_patterns || [],
        priority: flow.priority || 0,
        can_be_triggered: flow.can_be_triggered !== false,
        is_fallback: flow.is_fallback || false,
        status: flow.status
      });
    } else {
      setEditingFlow(null);
      setFormData(DEFAULT_FORM_DATA);
    }
    setKeywordInput('');
    setPhraseInput('');
    setPatternInput('');
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error('Nombre es requerido');
      return;
    }
    
    setFormLoading(true);
    try {
      if (editingFlow) {
        await axios.put(`${API_URL}/api/workflows/flows/${editingFlow.id}`, formData);
        toast.success('Flujo actualizado');
      } else {
        await axios.post(`${API_URL}/api/workflows/flows`, formData);
        toast.success('Flujo creado');
      }
      setShowForm(false);
      fetchFlows();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error guardando flujo');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (flowId) => {
    if (!window.confirm('¿Eliminar este flujo?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/workflows/flows/${flowId}`);
      toast.success('Flujo eliminado');
      fetchFlows();
    } catch (error) {
      toast.error('Error eliminando flujo');
    }
  };

  const addKeyword = () => {
    if (keywordInput.trim() && !formData.trigger_keywords.includes(keywordInput.trim())) {
      setFormData({
        ...formData,
        trigger_keywords: [...formData.trigger_keywords, keywordInput.trim()]
      });
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword) => {
    setFormData({
      ...formData,
      trigger_keywords: formData.trigger_keywords.filter(k => k !== keyword)
    });
  };

  const addPhrase = () => {
    if (phraseInput.trim() && !formData.trigger_phrases.includes(phraseInput.trim())) {
      setFormData({
        ...formData,
        trigger_phrases: [...formData.trigger_phrases, phraseInput.trim()]
      });
      setPhraseInput('');
    }
  };

  const removePhrase = (phrase) => {
    setFormData({
      ...formData,
      trigger_phrases: formData.trigger_phrases.filter(p => p !== phrase)
    });
  };

  const addPattern = () => {
    if (patternInput.trim()) {
      try {
        new RegExp(patternInput);
        if (!formData.trigger_patterns.includes(patternInput.trim())) {
          setFormData({
            ...formData,
            trigger_patterns: [...formData.trigger_patterns, patternInput.trim()]
          });
          setPatternInput('');
        }
      } catch (e) {
        toast.error('Patrón regex inválido');
      }
    }
  };

  const removePattern = (pattern) => {
    setFormData({
      ...formData,
      trigger_patterns: formData.trigger_patterns.filter(p => p !== pattern)
    });
  };

  const toggleTask = (taskId) => {
    if (formData.task_ids.includes(taskId)) {
      setFormData({
        ...formData,
        task_ids: formData.task_ids.filter(id => id !== taskId)
      });
    } else {
      setFormData({
        ...formData,
        task_ids: [...formData.task_ids, taskId]
      });
    }
  };

  const handleTest = async () => {
    if (!testMessage.trim()) return;
    
    try {
      const response = await axios.post(
        `${API_URL}/api/workflows/flows/${testDialog.id}/test?message=${encodeURIComponent(testMessage)}`
      );
      setTestResult(response.data);
    } catch (error) {
      toast.error('Error probando flujo');
    }
  };

  return (
    <div className="space-y-6" data-testid="flows-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Flujos de Trabajo</h1>
        <p className="text-gray-500 mt-1">Configura los flujos de trabajo del agente IA</p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && fetchFlows()}
              placeholder="Buscar flujos..."
              className="pl-10"
            />
          </div>
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="active">Activo</SelectItem>
            <SelectItem value="inactive">Inactivo</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={() => openForm()} className="bg-[#E63946] hover:bg-[#d32836]">
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Flujo
        </Button>
      </div>

      {/* Flows Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-[#E63946]" />
        </div>
      ) : flows.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {flows.map(flow => (
            <FlowCard
              key={flow.id}
              flow={flow}
              onEdit={openForm}
              onDelete={handleDelete}
              onTest={setTestDialog}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <GitBranch className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>No hay flujos configurados</p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="flex items-center px-4 text-sm">Página {page} de {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Flow Form Dialog */}
      <FlowFormDialog
        open={showForm}
        onOpenChange={setShowForm}
        editingFlow={editingFlow}
        formData={formData}
        setFormData={setFormData}
        tasks={tasks}
        keywordInput={keywordInput}
        setKeywordInput={setKeywordInput}
        phraseInput={phraseInput}
        setPhraseInput={setPhraseInput}
        patternInput={patternInput}
        setPatternInput={setPatternInput}
        formLoading={formLoading}
        onSubmit={handleSubmit}
        onAddKeyword={addKeyword}
        onRemoveKeyword={removeKeyword}
        onAddPhrase={addPhrase}
        onRemovePhrase={removePhrase}
        onAddPattern={addPattern}
        onRemovePattern={removePattern}
        onToggleTask={toggleTask}
      />

      {/* Test Flow Dialog */}
      <TestFlowDialog
        open={!!testDialog}
        onOpenChange={(open) => { if (!open) { setTestDialog(null); setTestResult(null); setTestMessage(''); } }}
        flow={testDialog}
        testMessage={testMessage}
        setTestMessage={setTestMessage}
        testResult={testResult}
        onTest={handleTest}
      />
    </div>
  );
};

export default FlowsPage;
